import { useState } from 'react';
import type { BoardroomDismissedNotice, BoardroomNotice } from '../../../types';

/**
 * What the room noticed, waiting to be looked at.
 *
 * NOTICED → ON THE BOARD → PINNED. Tags and links pile up while a dig runs and
 * things become obvious in them that nobody asked about: a complaint nine
 * people made that nothing addresses, a site in a third of the searches that is
 * not on the board, forty things sharing a tag with no card of their own. Until
 * this, spotting those meant reading a table and noticing.
 *
 * It is a TRAY, not a feed: each row is a thing you either want on the board or
 * do not, and saying no is remembered. Nothing here is a finding — every one of
 * them is counted off rows the room already has, with the count shown, so the
 * judgement stays the reader's.
 */
export default function NoticeTray({
  notices,
  dismissed = [],
  busy,
  undo,
  onTake,
  onDismiss,
  onPreview,
  onRestore,
}: {
  notices: BoardroomNotice[];
  /** What was set aside as not interesting, to bring back. */
  dismissed?: BoardroomDismissedNotice[];
  busy?: boolean;
  /** The one just set aside, while its Undo is offered. */
  undo?: BoardroomNotice | null;
  onTake: (n: BoardroomNotice) => void;
  onDismiss: (n: BoardroomNotice) => void;
  /** Show what taking it would put on the board, before deciding. */
  onPreview?: (n: BoardroomNotice) => void;
  /** Take a "not interesting" back. */
  onRestore?: (key: string) => void;
}) {
  const [showSetAside, setShowSetAside] = useState(false);
  if (!notices.length && !dismissed.length && !undo) return null;
  return (
    <div className="shrink-0" data-testid="notice-tray">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Noticed
        </span>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          {notices.length} thing{notices.length === 1 ? '' : 's'} the room spotted
        </span>
        {/* SAYING NO IS REMEMBERED, AND TAKEN BACK HERE. A "not interesting"
            was final, with no list of what had been set aside. */}
        {undo && onRestore && (
          <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="notice-undo">
            Set aside &quot;{undo.title}&quot;.{' '}
            <button type="button" onClick={() => onRestore(undo.key)} className="underline hover:no-underline">
              Undo
            </button>
          </span>
        )}
        {dismissed.length > 0 && onRestore && (
          <button
            type="button"
            onClick={() => setShowSetAside(v => !v)}
            className="ml-auto text-xs text-gray-500 underline hover:no-underline dark:text-gray-400"
            data-testid="notice-set-aside"
          >
            {showSetAside ? 'Hide set aside' : `Set aside (${dismissed.length})`}
          </button>
        )}
      </div>
      {showSetAside && onRestore && (
        <ul className="mb-2 space-y-1 rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs dark:border-gray-700 dark:bg-gray-800" data-testid="notice-set-aside-list">
          {dismissed.map(d => (
            <li key={d.key} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-200" title={d.title}>
                {d.title}
              </span>
              {!d.still && <span className="shrink-0 text-gray-400">no longer spotted</span>}
              {d.still && onPreview && (
                <button type="button" onClick={() => onPreview(d)} className="shrink-0 text-indigo-600 hover:underline dark:text-indigo-400">
                  Look
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => onRestore(d.key)}
                className="shrink-0 text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-400"
                data-testid={`notice-restore-${d.key}`}
              >
                Bring it back
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 pb-1">
        {notices.map(n => (
          <div
            key={n.key}
            className="min-w-[15rem] max-w-[19rem] shrink-0 rounded-lg border border-violet-200 bg-violet-50/60 px-2.5 py-2 dark:border-violet-800 dark:bg-violet-900/20"
            data-testid={`notice-${n.key}`}
          >
            {/* LOOK BEFORE DECIDING: the title opens what taking it would add. */}
            {onPreview ? (
              <button
                type="button"
                onClick={() => onPreview(n)}
                className="block w-full truncate text-left text-xs font-medium text-violet-900 hover:underline dark:text-violet-100"
                title={`${n.title} - see what it would put on the board`}
                data-testid={`notice-preview-${n.key}`}
              >
                {n.title}
              </button>
            ) : (
              <div className="truncate text-xs font-medium text-violet-900 dark:text-violet-100" title={n.title}>
                {n.title}
              </div>
            )}
            {/* THE COUNT, ALWAYS. "9 people said it" is why this is on screen,
                and without it the card is just an assertion. */}
            <div className="mt-0.5 text-xs text-violet-800/80 dark:text-violet-200/70">
              {n.why}
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onTake(n)}
                className="rounded border border-violet-400 bg-white/70 px-1.5 py-0.5 text-xs text-violet-900 hover:bg-white disabled:opacity-50 dark:border-violet-600 dark:bg-gray-800/60 dark:text-violet-100 dark:hover:bg-gray-800"
              >
                {n.verb}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onDismiss(n)}
                className="text-xs text-violet-700/70 hover:underline disabled:opacity-50 dark:text-violet-300/70"
              >
                Not interesting
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
